import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Producto } from '../../types';

interface ProductDetailProps {
  producto: Producto | null;
  loading: boolean;
  error: string | null;
  onEdit?: (producto: Producto) => void;
  onBack?: () => void;
}

export function ProductDetail({
  producto,
  loading,
  error,
  onEdit,
  onBack,
}: ProductDetailProps) {
  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!producto) {
    return <Alert severity="warning">Producto no encontrado</Alert>;
  }

  const margen = ((producto.precioVenta - producto.precioBase) / producto.precioBase * 100).toFixed(2);
  const stockStatus = producto.cantidadStock <= producto.stockMinimo;

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        {onBack && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
          >
            Volver
          </Button>
        )}
        {onEdit && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => onEdit(producto)}
          >
            Editar
          </Button>
        )}
      </Stack>

      <Typography variant="h5" sx={{ mb: 2 }}>
        {producto.nombre}
      </Typography>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
        <Box>
          <Typography variant="subtitle2" color="textSecondary">
            Código
          </Typography>
          <Typography variant="h6">{producto.codigo}</Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" color="textSecondary">
            Estado
          </Typography>
          <Typography variant="h6">
            {producto.activo ? '✓ Activo' : '✗ Inactivo'}
          </Typography>
        </Box>

        {producto.descripcion && (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography variant="subtitle2" color="textSecondary">
              Descripción
            </Typography>
            <Typography variant="body1">{producto.descripcion}</Typography>
          </Box>
        )}

        <Box>
          <Typography variant="subtitle2" color="textSecondary">
            Precio Base
          </Typography>
          <Typography variant="h6">${producto.precioBase.toFixed(2)}</Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" color="textSecondary">
            Precio Venta
          </Typography>
          <Typography variant="h6">${producto.precioVenta.toFixed(2)}</Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" color="textSecondary">
            Margen de Ganancia
          </Typography>
          <Typography variant="h6" color={Number(margen) >= 0 ? 'success.main' : 'error.main'}>
            {margen}%
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" color="textSecondary">
            Stock Actual
          </Typography>
          <Typography
            variant="h6"
            color={stockStatus ? 'error.main' : 'success.main'}
          >
            {producto.cantidadStock}
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" color="textSecondary">
            Stock Mínimo
          </Typography>
          <Typography variant="h6">{producto.stockMinimo}</Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" color="textSecondary">
            Status de Stock
          </Typography>
          <Typography
            variant="body1"
            color={stockStatus ? 'error.main' : 'success.main'}
          >
            {stockStatus ? 'Por debajo del mínimo' : 'Stock normal'}
          </Typography>
        </Box>

        <Box sx={{ gridColumn: '1 / -1' }}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Inventario por Color
          </Typography>
        </Box>

        {producto.colores && producto.colores.length > 0 ? (
          <TableContainer sx={{ gridColumn: '1 / -1' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Color</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Stock
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {producto.colores.map((color, idx) => (
                  <TableRow key={color.id || idx}>
                    <TableCell>{color.color}</TableCell>
                    <TableCell align="right">{color.stock}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography variant="body2" color="textSecondary">
              No hay colores registrados
            </Typography>
          </Box>
        )}

        <Box sx={{ gridColumn: '1 / -1' }}>
          <Divider sx={{ my: 2 }} />
        </Box>

        <Box>
          <Typography variant="caption" color="textSecondary">
            Creado
          </Typography>
          <Typography variant="body2">
            {new Date(producto.createdAt).toLocaleDateString()}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="textSecondary">
            Última actualización
          </Typography>
          <Typography variant="body2">
            {new Date(producto.updatedAt).toLocaleDateString()}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}
